import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Download,
  RefreshCw,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Package,
  DollarSign,
  Store,
  Truck,
  CheckCircle,
  XCircle,
} from "lucide-react";
import * as XLSX from "xlsx";

// Period shortcuts
const PERIOD_SHORTCUTS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

// Helper to calculate date range from shortcut
const getDateRange = (shortcut) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (shortcut) {
    case "today":
      return {
        from: today.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        from: yesterday.toISOString().split('T')[0],
        to: yesterday.toISOString().split('T')[0]
      };
    }
    case "this_week": {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return {
        from: startOfWeek.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };
    }
    case "last_week": {
      const startOfLastWeek = new Date(today);
      startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
      return {
        from: startOfLastWeek.toISOString().split('T')[0],
        to: endOfLastWeek.toISOString().split('T')[0]
      };
    }
    case "this_month": {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: startOfMonth.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };
    }
    case "last_month": {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        from: startOfLastMonth.toISOString().split('T')[0],
        to: endOfLastMonth.toISOString().split('T')[0]
      };
    }
    case "this_year": {
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return {
        from: startOfYear.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };
    }
    default:
      return { from: today.toISOString().split('T')[0], to: today.toISOString().split('T')[0] };
  }
};

export const FoodDeliveryOrdersReport = ({ onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_count: 0,
    total_pages: 1
  });
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  const [dateFrom, setDateFrom] = useState(() => getDateRange("today").from);
  const [dateTo, setDateTo] = useState(() => getDateRange("today").to);
  const [cityFilter, setCityFilter] = useState("all");
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [riderFilter, setRiderFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Search states for filters
  const [citySearch, setCitySearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [riderSearch, setRiderSearch] = useState("");

  // Fetch filter options
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchOrders();
    fetchSummary();
  }, [dateFrom, dateTo, cityFilter, restaurantFilter, riderFilter, statusFilter, pagination.page]);

  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${API}/quick-commerce/reports/orders/filters`);
      setFilters(res.data);
    } catch (err) {
      console.error("Failed to fetch filters:", err);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", pagination.page.toString());
      params.append("page_size", "50");
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (cityFilter !== "all") params.append("city_id", cityFilter);
      if (restaurantFilter !== "all") params.append("restaurant_id", restaurantFilter);
      if (riderFilter !== "all") params.append("driver_id", riderFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const res = await axios.get(`${API}/quick-commerce/reports/orders?${params}`);
      setOrders(res.data.orders || []);
      setPagination(res.data.pagination || pagination);
    } catch (err) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (cityFilter !== "all") params.append("city_id", cityFilter);
      if (restaurantFilter !== "all") params.append("restaurant_id", restaurantFilter);
      
      const res = await axios.get(`${API}/quick-commerce/reports/orders/summary?${params}`);
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period !== "custom") {
      const range = getDateRange(period);
      setDateFrom(range.from);
      setDateTo(range.to);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const exportToExcel = () => {
    if (!orders.length) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = orders.map(o => ({
      "City": o.city,
      "Restaurant": o.restaurant_name,
      "Restaurant Phone": o.restaurant_phone,
      "Customer": o.customer_name,
      "Customer Phone": o.customer_phone,
      "Order Total (₹)": o.order_total,
      "Store Payout (₹)": o.store_payout,
      "Delivery Charges (₹)": o.delivery_charges,
      "COD": o.is_cod ? "Yes" : "No",
      "CDC": o.is_cdc ? "Yes" : "No",
      "Order Placed": formatDateTime(o.placed_at),
      "Delivered": formatDateTime(o.delivered_at),
      "Status": o.status,
      "Rider": o.rider_name || "-",
      "Rider Phone": o.rider_phone || "-"
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Food Delivery Orders");
    XLSX.writeFile(wb, `Food_Delivery_Orders_${dateFrom}_to_${dateTo}.xlsx`);
    toast.success("Report exported successfully");
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      "Ride Placed": "bg-amber-100 text-amber-700",
      "Ride Accepted": "bg-purple-100 text-purple-700",
      "Ride Active": "bg-blue-100 text-blue-700",
      "Ride InProgress": "bg-cyan-100 text-cyan-700",
      "Ride Completed": "bg-green-100 text-green-700",
      "Ride Canceled": "bg-red-100 text-red-700"
    };
    return statusColors[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-4" data-testid="food-delivery-orders-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl font-bold">Food Delivery Orders Report</h2>
            <p className="text-sm text-muted-foreground">
              {pagination.total_count} orders found
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchOrders}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground">Total Orders</p>
                <p className="text-lg font-bold">{summary.total_orders}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground">Order Value</p>
                <p className="text-lg font-bold">₹{summary.total_order_value?.toFixed(0)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground">Store Payout</p>
                <p className="text-lg font-bold">₹{summary.total_store_payout?.toFixed(0)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-amber-500" />
              <div>
                <p className="text-xs text-muted-foreground">Delivery</p>
                <p className="text-lg font-bold">₹{summary.total_delivery_charges?.toFixed(0)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-lg font-bold">{summary.completed_orders}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <div>
                <p className="text-xs text-muted-foreground">Cancelled</p>
                <p className="text-lg font-bold">{summary.cancelled_orders}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-amber-500" />
              <div>
                <p className="text-xs text-muted-foreground">COD Orders</p>
                <p className="text-lg font-bold">{summary.cod_orders}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground">CDC Orders</p>
                <p className="text-lg font-bold">{summary.cdc_orders}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {/* Period Shortcuts */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Period</label>
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_SHORTCUTS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">From</label>
              <Input 
                type="date" 
                value={dateFrom} 
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setSelectedPeriod("custom");
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-9"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">To</label>
              <Input 
                type="date" 
                value={dateTo} 
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setSelectedPeriod("custom");
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-9"
              />
            </div>

            {/* City Filter */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">City</label>
              <Select value={cityFilter} onValueChange={(v) => {
                setCityFilter(v);
                setCitySearch("");
                setPagination(prev => ({ ...prev, page: 1 }));
              }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-2">
                    <Input 
                      placeholder="Search city..." 
                      value={citySearch}
                      onChange={(e) => setCitySearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <SelectItem value="all">All Cities</SelectItem>
                  {filters?.cities
                    ?.filter(c => c.name?.toLowerCase().includes(citySearch.toLowerCase()))
                    ?.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Restaurant Filter */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Restaurant</label>
              <Select value={restaurantFilter} onValueChange={(v) => {
                setRestaurantFilter(v);
                setRestaurantSearch("");
                setPagination(prev => ({ ...prev, page: 1 }));
              }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Restaurants" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-2">
                    <Input 
                      placeholder="Search name or phone..." 
                      value={restaurantSearch}
                      onChange={(e) => setRestaurantSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {filters?.restaurants
                    ?.filter(r => 
                      r.name?.toLowerCase().includes(restaurantSearch.toLowerCase()) ||
                      r.phone?.includes(restaurantSearch)
                    )
                    ?.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} ({r.order_count})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rider Filter */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Rider</label>
              <Select value={riderFilter} onValueChange={(v) => {
                setRiderFilter(v);
                setRiderSearch("");
                setPagination(prev => ({ ...prev, page: 1 }));
              }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Riders" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-2">
                    <Input 
                      placeholder="Search name or phone..." 
                      value={riderSearch}
                      onChange={(e) => setRiderSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <SelectItem value="all">All Riders</SelectItem>
                  {filters?.riders
                    ?.filter(r => 
                      r.name?.toLowerCase().includes(riderSearch.toLowerCase()) ||
                      r.phone?.includes(riderSearch)
                    )
                    ?.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} ({r.phone})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={(v) => {
                setStatusFilter(v);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {filters?.statuses?.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>City</TableHead>
                <TableHead>Restaurant</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Order Total</TableHead>
                <TableHead className="text-right">Store Payout</TableHead>
                <TableHead className="text-right">Delivery</TableHead>
                <TableHead className="text-center">COD</TableHead>
                <TableHead className="text-center">CDC</TableHead>
                <TableHead>Order Placed</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Rider</TableHead>
                <TableHead>Rider Phone</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-8 text-muted-foreground">
                    No orders found for the selected filters
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="text-sm">{order.city}</TableCell>
                    <TableCell>
                      <span className="font-medium text-sm">{order.restaurant_name}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{order.restaurant_phone}</TableCell>
                    <TableCell className="text-sm">{order.customer_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{order.customer_phone}</TableCell>
                    <TableCell className="text-right font-medium">₹{order.order_total}</TableCell>
                    <TableCell className="text-right text-purple-600">
                      {order.store_payout > 0 ? `₹${order.store_payout}` : "-"}
                    </TableCell>
                    <TableCell className="text-right text-green-600">₹{order.delivery_charges}</TableCell>
                    <TableCell className="text-center">
                      {order.is_cod ? (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">COD</span>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {order.is_cdc ? (
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">CDC</span>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(order.placed_at)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDateTime(order.delivered_at)}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(order.status)}`}>
                        {order.status?.replace("Ride ", "")}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{order.rider_name || "-"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{order.rider_phone || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} orders)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FoodDeliveryOrdersReport;
